import { Link } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DeviceStatusBadge, SerializedItemStatusBadge } from '../shared/StatusBadge';
import { SerialsEmptyState } from '../shared/EmptyState';
import type { SerializedItem } from '../../types';

interface SerializedItemsTableProps {
  items: SerializedItem[];
  onTransfer: (item: SerializedItem) => void;
  onChangeStatus: (item: SerializedItem) => void;
  /** Whether filters are currently applied */
  isFiltered?: boolean;
  /** Callback to clear filters */
  onClearFilters?: () => void;
}

export function SerializedItemsTable({
  items,
  onTransfer,
  onChangeStatus,
  isFiltered,
  onClearFilters,
}: SerializedItemsTableProps) {
  if (items.length === 0) {
    return (
      <SerialsEmptyState
        isFiltered={isFiltered}
        secondaryAction={isFiltered && onClearFilters ? {
          label: 'Clear Filters',
          onClick: onClearFilters,
        } : undefined}
      />
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Serial Number</TableHead>
          <TableHead>SKU</TableHead>
          <TableHead>Location</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>5S Status</TableHead>
          <TableHead>Calibration</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((item) => (
          <TableRow key={item.id}>
            <TableCell>
              <Link
                to={`/wms/serials/${item.id}`}
                className="font-mono font-medium text-primary hover:underline"
              >
                {item.serial_number}
              </Link>
              {item.mac_address && (
                <div className="text-xs text-muted-foreground font-mono">
                  MAC: {item.mac_address}
                </div>
              )}
              {item.dev_id && (
                <div className="text-xs text-muted-foreground font-mono">
                  ID: {item.dev_id}
                </div>
              )}
            </TableCell>
            <TableCell>
              <div className="font-medium">{item.sku_code}</div>
            </TableCell>
            <TableCell>
              {item.full_location_code ? (
                <Badge variant="outline" className="font-mono">
                  {item.full_location_code}
                </Badge>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </TableCell>
            <TableCell>
              <SerializedItemStatusBadge status={item.item_status} />
            </TableCell>
            <TableCell>
              {item.device_status ? (
                <DeviceStatusBadge status={item.device_status} />
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </TableCell>
            <TableCell>
              {item.calibration_due_date ? (
                <CalibrationStatus dueDate={item.calibration_due_date} />
              ) : (
                <span className="text-muted-foreground text-sm">N/A</span>
              )}
            </TableCell>
            <TableCell className="text-right">
              <div className="flex justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onChangeStatus(item)}
                  disabled={!item.device_status && item.item_status !== 'in_stock'}
                >
                  Status
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onTransfer(item)}
                  disabled={item.item_status !== 'in_stock' && item.item_status !== 'reserved'}
                >
                  Transfer
                </Button>
                <Button variant="ghost" size="sm" asChild>
                  <Link to={`/wms/serials/${item.id}`}>View</Link>
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function CalibrationStatus({ dueDate }: { dueDate: string }) {
  const due = new Date(dueDate);
  const today = new Date();
  const daysUntilDue = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  let variant: 'default' | 'destructive' | 'secondary' = 'secondary';
  let label = `Due ${due.toLocaleDateString()}`;

  if (daysUntilDue < 0) {
    variant = 'destructive';
    label = `Overdue (${Math.abs(daysUntilDue)}d)`;
  } else if (daysUntilDue <= 30) {
    variant = 'default';
    label = `Due in ${daysUntilDue}d`;
  }

  return (
    <Badge variant={variant} className="text-xs">
      {label}
    </Badge>
  );
}
